import React, { useState, useEffect } from 'react'

import useScrollReset from '../../useScrollReset'

import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { Flex } from 'components/Box'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import EmptyState from 'modules/shares/components/EmptyState'

import { Layout } from '../../components/Layout'
import { ListGrid, ListGridItem, GroupName, Group } from '../../components/Grid'
import {
  ColorContainer,
  ColorSwatch,
  ColorSwatchMask,
  ColorDetailName,
  ColorDetail,
} from './ColorVariables.styles'

import { useSearchComponentsContext } from '../../SearchCwvContext'
import { useGetColorVariables } from '../../operations'
import { groupStyles } from '../../utils'
import { roundWithLocale } from 'utils/math'

const ColorVariablesList = ({
  searchCwv,
  path,
}: {
  searchCwv: string
  path: string | null
}) => {
  const { entries, meta, fetchMore, loading, filter } = useGetColorVariables(
    searchCwv
  )
  useScrollReset()

  useEffect(() => {
    filter(path === 'View All' ? null : path)
    // we do NOT want to add filter as a dependency here
    // eslint-disable-next-line
  }, [path])

  if (loading) {
    return (
      <Flex justifyContent="center" flex="auto">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  const colorGroups = groupStyles(entries)
  const groupsNames = Object.keys(colorGroups)

  if (groupsNames.length === 0) {
    if (searchCwv !== '') {
      return (
        <EmptyState
          title="No search results"
          description="No matching items."
          icon="search"
        />
      )
    }

    return (
      <EmptyState
        title="No Color Variables"
        description="This Document does not contain any Color Variables."
        icon="colorVariable"
      />
    )
  }

  const convertColor = (color: string) => {
    if (color.includes('#')) {
      return color
    }

    // stolen from https://stackoverflow.com/questions/49974145/how-to-convert-rgba-to-hex-color-code-using-javascript
    const rgba = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',')

    let hex = `#${(
      (1 << 24) +
      (parseInt(rgba[0], 10) << 16) +
      (parseInt(rgba[1], 10) << 8) +
      parseInt(rgba[2], 10)
    )
      .toString(16)
      .slice(1)}`

    if (rgba[4]) {
      const alpha = Math.round(0o1 * 255)
      const hexAlpha = (alpha + 0x10000).toString(16).substr(-2).toUpperCase()
      hex += hexAlpha
    }

    return hex
  }

  return (
    <InfiniteList
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'version', 'document', 'components', 'entries'],
        { after: meta?.after || null }
      )}>
      {groupsNames.map(groupName => (
        <Group key={groupName}>
          <GroupName groups={groupName} />
          <ListGrid>
            {colorGroups[groupName].map((color: any) => (
              <ListGridItem key={color.identifier}>
                <ColorContainer>
                  <ColorSwatch backgroundColor={color.rgbaValue!} />
                  <ColorSwatchMask />
                </ColorContainer>
                <ColorDetailName>{color.name}</ColorDetailName>{' '}
                <ColorDetail>
                  {convertColor(color.rgbaValue)} —{' '}
                  {roundWithLocale(color.alpha * 100, 2)} %
                </ColorDetail>
              </ListGridItem>
            ))}
          </ListGrid>
        </Group>
      ))}
    </InfiniteList>
  )
}

export const ColorVariables: React.FC = () => {
  const { searchCwv } = useSearchComponentsContext()
  const [selected, setSelected] = useState('View All')

  return (
    <Layout onSelect={setSelected} type="COLOR_VARIABLE" selected={selected}>
      <ColorVariablesList searchCwv={searchCwv} path={selected} />
    </Layout>
  )
}
